/**
 * Umbrella3D 组件 — 3D 伞模型展示
 * 封装 Three.js 场景交互层
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { createUmbrellaModel } from '../three/UmbrellaModel.js';
import { embeddedModels, embeddedTextures } from '../app/modelData.js';

/**
 * 初始化伞的3D展示
 * @param {HTMLElement} container - 容器DOM
 * @param {Object} data - 伞数据
 * @returns {Object} 场景控制对象
 */
export function initUmbrella3D(container, data) {
  const width = container.clientWidth;
  const height = container.clientHeight;

  // 场景
  const scene = new THREE.Scene();
  // 背景透明，让CSS背景图显示
  scene.background = null;

  // 相机
  // 相机
  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
  camera.position.set(0, 1.5, 4);

  // 渲染器
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance'
  });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  // 外部 GLB 模型：琉璃质感（glaze）用更高曝光还原清透感，其余可按条目自定义曝光
  renderer.toneMappingExposure = data.modelEffect === 'glaze' ? 1.45 : (data.exposure ?? 1.2);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  // 控制器
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  // 允许自由平移：放大后模型超出视口时，可拖动调整位置
  controls.enablePan = true;
  controls.screenSpacePanning = true; // 屏幕空间平移：上下拖动即上下移动画面，操作更直观
  controls.panSpeed = 1.0;
  controls.minDistance = 2;
  controls.maxDistance = 12;
  controls.minPolarAngle = Math.PI * 0.2;
  controls.maxPolarAngle = Math.PI * 0.6;
  controls.autoRotate = false;
  controls.target.set(0, 1, 0);

  // 灯光系统
  setupLighting(scene, data);

  // 外部 GLB 模型（磨砂琉璃）：环境贴图提供琉璃表面光泽
  let pmrem = null;
  if (data.modelFile && !data.noModel) {
    pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  }

  // 创建伞模型（有外部模型文件则异步加载，否则程序化生成）
  let umbrellaGroup = null;

  // 基准取景（frameModel 计算）+ 编辑器视角微调参数（data.modelView 固化）
  const baseView = { dist: 0, target: new THREE.Vector3(), dir: new THREE.Vector3(0, 0, 1) };
  let viewAdjust = { scale: 1, offsetX: 0, offsetY: 0, ...(data.modelView || {}) };

  function applyViewAdjust() {
    if (!baseView.dist) return;
    // 宽屏（桌面布局 ≥900px）可用 scaleWide/offsetXWide/offsetYWide 覆盖：
    // 桌面画布更高，同一 scale 下模型显得更大，部分伞需单独收敛避免压住伞名横幅
    const isWide = window.matchMedia('(min-width: 900px)').matches;
    const adjScale = (isWide && viewAdjust.scaleWide) || viewAdjust.scale || 1;
    const adjX = (isWide && viewAdjust.offsetXWide !== undefined) ? viewAdjust.offsetXWide : viewAdjust.offsetX;
    const adjY = (isWide && viewAdjust.offsetYWide !== undefined) ? viewAdjust.offsetYWide : viewAdjust.offsetY;
    const dist = baseView.dist / adjScale;
    const fovRad = (camera.fov * Math.PI) / 180;
    const visH = 2 * dist * Math.tan(fovRad / 2);
    const visW = visH * camera.aspect;
    const dx = (adjX / 100) * visW;   // offsetX：占视口宽度的百分比，右为正
    const dy = -(adjY / 100) * visH;  // offsetY：占视口高度的百分比；注意：正值=画面上移，负值=画面下移（摄像机随目标点同向平移，视觉方向相反）
    controls.target.set(baseView.target.x + dx, baseView.target.y + dy, baseView.target.z);
    camera.position.set(
      controls.target.x + baseView.dir.x * dist,
      controls.target.y + baseView.dir.y * dist,
      controls.target.z + baseView.dir.z * dist
    );
    camera.lookAt(controls.target);
  }

  // 依据模型包围盒自动取景：目标对准模型中心，距离适配模型大小与屏幕宽高比
  function frameModel(group) {
    const bbox = new THREE.Box3().setFromObject(group);
    const bboxCenter = bbox.getCenter(new THREE.Vector3());
    const bboxSize = bbox.getSize(new THREE.Vector3());
    const fovRad = (camera.fov * Math.PI) / 180;
    const fitH = (bboxSize.y * 0.5) / Math.tan(fovRad / 2);
    const fitW = (bboxSize.x * 0.5) / (Math.tan(fovRad / 2) * camera.aspect);
    const viewDist = Math.max(fitH, fitW) * 2.05;
    // 仰角系数：扁平伞型（如余杭伞）可调大，让初始视角看到伞面贴图
    const elevation = bboxSize.y * 0.18 * (data.viewElev ?? 1);
    // 取景上移：模型整体上移一点，落在画作前方偏上位置
    const raise = bboxSize.y * 0.08;
    controls.target.set(bboxCenter.x, bboxCenter.y - raise, bboxCenter.z);
    camera.position.set(
      bboxCenter.x + viewDist * 0.05,
      bboxCenter.y + elevation,
      bboxCenter.z + viewDist
    );
    // 记录基准取景，供编辑器在基准之上做缩放/平移微调
    baseView.target.copy(controls.target);
    baseView.dir.copy(camera.position).sub(controls.target);
    baseView.dist = baseView.dir.length();
    baseView.dir.normalize();
    applyViewAdjust();
  }

  if (data.noModel) {
    // 该时期不做3D模型（如汉代车盖）：画布保持透明，突出画作主视觉
  } else if (data.modelFile) {
    loadExternalUmbrellaModel(data, renderer.capabilities.getMaxAnisotropy()).then((group) => {
      umbrellaGroup = group;
      scene.add(group);
      frameModel(group);
    });
  } else {
    umbrellaGroup = createUmbrellaModel(data);
    scene.add(umbrellaGroup);
    frameModel(umbrellaGroup);
  }

  // 地面阴影（无3D模型时不渲染）
  if (!data.noModel) {
    const groundGeo = new THREE.PlaneGeometry(10, 10);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.08 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);
  }

  // 动画循环
  let animationId;
  const clock = new THREE.Clock();

  function animate() {
    animationId = requestAnimationFrame(animate);
    const elapsed = clock.getElapsedTime();

    // 轻微浮动效果（模型未加载完成时跳过）
    if (umbrellaGroup) {
      umbrellaGroup.position.y = Math.sin(elapsed * 0.5) * 0.05;
    }

    controls.update();
    renderer.render(scene, camera);
  }
  animate();

  // 响应式
  let lastWide = window.matchMedia('(min-width: 900px)').matches;
  function onResize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    // 仅在跨越桌面/手机布局分界时重取景（避免手机滚动时地址栏伸缩触发 resize 重置用户拖拽视角）
    const nowWide = window.matchMedia('(min-width: 900px)').matches;
    if (nowWide !== lastWide) {
      lastWide = nowWide;
      applyViewAdjust();
    }
  }
  window.addEventListener('resize', onResize);

  // 双击自动旋转
  renderer.domElement.addEventListener('dblclick', () => {
    controls.autoRotate = !controls.autoRotate;
    controls.autoRotateSpeed = controls.autoRotate ? 2 : 0;
  });

  return {
    scene,
    camera,
    renderer,
    controls,
    umbrellaGroup,
    setViewAdjust: (adj = {}) => {
      viewAdjust = { ...viewAdjust, ...adj };
      applyViewAdjust();
    },
    getViewAdjust: () => ({ ...viewAdjust }),
    dispose: () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      if (scene.environment) {
        scene.environment.dispose();
        scene.environment = null;
      }
      if (pmrem) pmrem.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const disposeMat = (m) => {
            ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'].forEach(k => {
              if (m[k]) m[k].dispose();
            });
            m.dispose();
          };
          if (Array.isArray(obj.material)) {
            obj.material.forEach(disposeMat);
          } else {
            disposeMat(obj.material);
          }
        }
      });
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    }
  };
}

/**
 * base64 data URI → ArrayBuffer（沙箱禁 fetch，模型内嵌解析）
 */
function dataUriToArrayBuffer(uri) {
  const b64 = uri.slice(uri.indexOf(',') + 1);
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

// 小红书版贴图已改为 base64 内嵌在 modelData.js（embeddedTextures），
// 运行时走纯内存通道解码，见 loadExternalModel 内注释。

/**
 * 加载外部 GLB 伞模型（贴图内嵌，保留原始 PBR 材质与法线）
 * 归一化尺寸（最大边约2.0单位）、水平居中、底部落地
 * 优先使用 base64 内嵌模型（小红书包免 fetch）；完整版无内嵌时
 * 回退到 fetch 加载 modelFile，并按需动态引入 meshopt 解码器
 * （动态引入保证小红书构建产物不携带 WASM 解码器代码）。
 * @param {Object} data - 含 modelFile 的伞数据
 * @param {number} [maxAnisotropy] - 渲染器支持的各向异性过滤上限（斜角观看时贴图更清晰）
 * @returns {Promise<THREE.Group>}
 */
async function loadExternalUmbrellaModel(data, maxAnisotropy = 1) {
  // base64 内嵌模型是「小红书版」的减面/降质压缩副本（包体限制）；
  // 仅 xhs 构建模式使用。完整版一律加载原始高清 GLB，保证贴图清晰度。
  const IS_XHS = import.meta.env.MODE === 'xhs';
  const uri = IS_XHS ? embeddedModels[data.id] : null;
  let loader;
  if (uri) {
    // 小红书安卓沙箱禁 fetch：GLTFLoader 默认用 ImageBitmapLoader 解码贴图，
    // 其内部走 fetch(blob:) 会被沙箱拦截，导致贴图静默丢失（白模）。
    // 构造前临时屏蔽 createImageBitmap，强制回退 TextureLoader（<img> 解码 blob:，不经 fetch）。
    // iOS 沙箱本就因 Safari UA 走 TextureLoader，不受影响。
    const origCIB = window.createImageBitmap;
    window.createImageBitmap = undefined;
    loader = new GLTFLoader();
    window.createImageBitmap = origCIB;
  } else {
    loader = new GLTFLoader();
  }
  let gltf;
  if (uri) {
    const buf = dataUriToArrayBuffer(uri);
    gltf = await new Promise((resolve, reject) => {
      loader.parse(buf, '', resolve, reject);
    });
  } else {
    // 完整版：从 modelFile 加载（元/明/民国八角等大模型，meshopt 压缩）
    const { MeshoptDecoder } = await import('three/examples/jsm/libs/meshopt_decoder.module.js');
    loader.setMeshoptDecoder(MeshoptDecoder);
    gltf = await loader.loadAsync(data.modelFile);
  }
  return (async (gltf) => {
    const obj = gltf.scene;

    const isGlaze = data.modelEffect === 'glaze';
    const aniso = Math.min(8, maxAnisotropy || 1);

    // 小红书版：贴图走纯内存通道（规范：内存对象作为纹理 ✅）。
    // 沙箱对 JS 动态创建 Image 加载包内 jpg 也不放行（白膜根因），
    // 改为 base64 → Uint8Array → Blob → createImageBitmap → THREE.Texture，
    // 全程无 URL / 无 fetch / 无 <img>。材质上 xhsTex 标记与
    // KHR_texture_transform 平铺参数由 scripts/split-embed-textures.mjs 写入。
    if (uri) {
      const texData = embeddedTextures[data.id];
      if (texData) {
        const loadTex = async (dataUri, srgb, tf) => {
          const buf = dataUriToArrayBuffer(dataUri);
          const blob = new Blob([buf], { type: 'image/jpeg' });
          let bitmap;
          try {
            bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });
          } catch (e) {
            bitmap = await createImageBitmap(blob);
          }
          const t = new THREE.Texture(bitmap);
          t.flipY = false;   // ImageBitmap 上传不翻转，与 glTF UV 约定一致
          if (srgb) t.colorSpace = THREE.SRGBColorSpace;
          t.anisotropy = aniso;
          t.wrapS = t.wrapT = THREE.RepeatWrapping;   // 平铺贴图必须可重复（西湖绸伞 16×）
          if (tf) {
            t.repeat.set(tf.scale[0], tf.scale[1]);
            t.offset.set(tf.offset[0], tf.offset[1]);
            t.rotation = tf.rotation || 0;
          }
          t.needsUpdate = true;
          return t;
        };
        const jobs = [];
        obj.traverse((child) => {
          if (!child.isMesh) return;
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) {
            if (!m || !m.userData || !m.userData.xhsTex) continue;
            const tf = m.userData.xhsTexTransform || null;
            if (texData.basecolor) jobs.push(loadTex(texData.basecolor, true, tf).then((t) => { m.map = t; }));
            if (texData.normal) jobs.push(loadTex(texData.normal, false, tf).then((t) => { m.normalMap = t; }));
            if (texData.mr) jobs.push(loadTex(texData.mr, false, tf).then((t) => { m.metalnessMap = t; m.roughnessMap = t; }));
            if (texData.emissive) jobs.push(loadTex(texData.emissive, true, tf).then((t) => { m.emissiveMap = t; }));
          }
        });
        await Promise.all(jobs);
        obj.traverse((child) => {
          if (child.isMesh) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((m) => { if (m) m.needsUpdate = true; });
          }
        });
      }
    }

    obj.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        const m = child.material;
        if (m) {
          // 各向异性过滤：伞面大角度斜视时贴图不糊（清晰度关键项）
          ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'].forEach((k) => {
            if (m[k]) m[k].anisotropy = aniso;
          });
        }
        if (m && isGlaze) {
          // 磨砂琉璃质感（仅宋代琉璃伞）：半透明 + 环境光泽 + 适度自发光
          m.transparent = true;
          m.opacity = 0.72;
          m.depthWrite = false;
          m.metalness = 0;
          m.envMapIntensity = 0.7;
          if (m.emissiveMap) {
            m.emissiveIntensity = 1.1;
          }
          m.needsUpdate = true;
        }
      }
    });

    // 归一化：按最大边缩放到适中尺寸
    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const scale = 2.0 / Math.max(size.x, size.y, size.z);
    obj.scale.setScalar(scale);

    // 水平居中、底部落地
    const bbox2 = new THREE.Box3().setFromObject(obj);
    const center = bbox2.getCenter(new THREE.Vector3());
    obj.position.x -= center.x;
    obj.position.z -= center.z;
    obj.position.y -= bbox2.min.y;

    const wrapper = new THREE.Group();
    wrapper.add(obj);
    return wrapper;
  })(gltf);
}

/**
 * 设置灯光
 */
function setupLighting(scene, data) {
  // 环境光（外部 GLB 模型需要更亮环境光还原琉璃清透感）
  const ambientLight = new THREE.AmbientLight(0xffffff, data && data.modelFile ? 1.0 : 0.4);
  scene.add(ambientLight);

  // 主光源（暖色，模拟室内灯光）
  const mainLight = new THREE.DirectionalLight(0xfff5e6, 1.5);
  mainLight.position.set(3, 5, 3);
  mainLight.castShadow = true;
  mainLight.shadow.mapSize.width = 1024;
  mainLight.shadow.mapSize.height = 1024;
  mainLight.shadow.camera.near = 0.5;
  mainLight.shadow.camera.far = 20;
  scene.add(mainLight);

  // 补光（冷色，增加层次）
  const fillLight = new THREE.DirectionalLight(0xe6eeff, 0.5);
  fillLight.position.set(-3, 3, -2);
  scene.add(fillLight);

  // 轮廓光
  const rimLight = new THREE.SpotLight(0xffeedd, 0.8);
  rimLight.position.set(0, 4, -4);
  rimLight.lookAt(0, 1, 0);
  scene.add(rimLight);
}
